import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import './LegalStyles.scss';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// PRIVACY POLICY COMPONENT
// ============================================
export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="sds_legal-wrapper">
      {/* SEO Meta */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions Privacy Policy</h1>
        <meta name="description" content="HDT Solutions privacy policy. Learn how we collect, use, and protect your personal information." />
      </div>

      <div className="container">
        <AnimatedSection className="sds_legal-header section">
          <div className="has-text-centered">
            <motion.div 
              className="sds_legal-icon"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              🔒
            </motion.div>
            <h1 className="title is-1 sds_legal-title">Privacy Policy</h1>
            <p className="subtitle is-5 sds_legal-subtitle">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="sds_legal-intro">
              At HDT Solutions , we are committed to protecting your privacy and ensuring the security of your personal information.
            </p>
          </div>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" animate="visible">
          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">1. Information We Collect</h2>
            <div className="content">
              <h3 className="title is-5">1.1 Personal Information</h3>
              <p>We collect information that you provide directly to us, including:</p>
              <ul>
                <li>Name and contact information (email, phone number, address)</li>
                <li>Business information (company name, designation, industry)</li>
                <li>Payment and billing information</li>
                <li>Account credentials and preferences</li>
                <li>Communication history and support requests</li>
              </ul>

              <h3 className="title is-5 mt-5">1.2 Automatically Collected Information</h3>
              <p>When you use our services, we automatically collect:</p>
              <ul>
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Log files and technical data</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">2. How We Use Your Information</h2>
            <div className="content">
              <p>We use your information for the following purposes:</p>
              <ul>
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve our services</li>
                <li><strong>Communication:</strong> To respond to inquiries and send service updates</li>
                <li><strong>Security:</strong> To protect against fraud and unauthorized access</li>
                <li><strong>Analytics:</strong> To understand usage patterns and improve user experience</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and regulations</li>
                <li><strong>Marketing:</strong> To send promotional materials (with your consent)</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">3. Information Sharing and Disclosure</h2>
            <div className="content">
              <p>We do not sell your personal information. We may share your information with:</p>
              <ul>
                <li><strong>Service Providers:</strong> Third-party vendors who assist in service delivery</li>
                <li><strong>Business Partners:</strong> With your consent, for joint services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">4. Data Security</h2>
            <div className="content">
              <p>We implement industry-standard security measures to protect your information:</p>
              <ul>
                <li>256-bit SSL/TLS encryption for data transmission</li>
                <li>Encrypted storage of sensitive information</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <div className="notification is-warning is-light mt-4">
                <p><strong>Note:</strong> While we implement robust security measures, no system is completely secure. We cannot guarantee absolute security of your information.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">5. Your Rights and Choices</h2>
            <div className="content">
              <p>You have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                <li><strong>Objection:</strong> Object to processing of your information</li>
              </ul>
              <p className="mt-4">To exercise these rights, contact us at <a href="mailto:privacy@hdtsolution.com">privacy@hdtsolution.com</a></p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">6. Data Retention</h2>
            <div className="content">
              <p>We retain your information for as long as necessary to:</p>
              <ul>
                <li>Provide services and fulfill contractual obligations</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain business records and analytics</li>
              </ul>
              <p className="mt-4">Typical retention periods: Active accounts (duration of service), Inactive accounts (3 years), Transaction records (7 years for tax purposes)</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">7. Children's Privacy</h2>
            <div className="content">
              <p>Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it promptly.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">8. International Data Transfers</h2>
            <div className="content">
              <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in compliance with applicable data protection laws.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">9. Changes to Privacy Policy</h2>
            <div className="content">
              <p>We may update this Privacy Policy periodically. We will notify you of material changes by:</p>
              <ul>
                <li>Posting the updated policy on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying prominent notices in our application</li>
              </ul>
              <p className="mt-4">Continued use of our services after changes constitutes acceptance of the updated policy.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-contact">
            <h2 className="title is-3 sds_legal-section-title">10. Contact Us</h2>
            <div className="content">
              <p>For questions about this Privacy Policy or our data practices, contact us at:</p>
              <div className="box sds_legal-contact-box">
                <p><strong>HDT Solutions </strong></p>
                <p>📧 Email: <a href="mailto:sds@hdtsolution.com">privacy@hdtsolution.com</a></p>
                <p>📱 Phone: <a href="tel:+91 9553896536">{InfoService.mobile_no_1}</a></p>
                            <p>🌐 Website: <a href="https://hdtsecure.com/">hdtsecure.com</a></p>

              </div>
            </div>
          </AnimatedSection>
        </motion.div>
      </div>
    </div>
  );
};
export default PrivacyPolicy;